import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { 
  FaLeaf, 
  FaTag, 
  FaHeadset,
  FaCheese,
  FaDrumstickBite,
  FaBreadSlice,
  FaCoffee,
  FaBoxOpen,
  FaCookie
} from 'react-icons/fa';
import brandLogos from '../data/brandLogos.json';
import './Home.css';

const Home = () => {
  const { t } = useTranslation();

  const getCategoryKey = (index) => {
    const keys = ['dairy', 'meat', 'bakery', 'beverages', 'dry', 'snacks'];
    return keys[index];
  };

  const features = [
    {
      icon: <FaLeaf />,
      title: t('features.quality.title'),
      description: t('features.quality.description')
    },
    {
      icon: <FaTag />,
      title: t('features.prices.title'),
      description: t('features.prices.description')
    },
    {
      icon: <FaHeadset />,
      title: t('features.support.title'),
      description: t('features.support.description')
    }
  ];

  const categories = [
    { icon: <FaCheese />, title: t('products.categories.dairy.title') },
    { icon: <FaDrumstickBite />, title: t('products.categories.meat.title') },
    { icon: <FaBreadSlice />, title: t('products.categories.bakery.title') },
    { icon: <FaCoffee />, title: t('products.categories.beverages.title') },
    { icon: <FaBoxOpen />, title: t('products.categories.dry.title') },
    { icon: <FaCookie />, title: t('products.categories.snacks.title') }
  ];

  return (
    <div className="home">
      {/* Hero Section */}
      <section className="hero">
        <video 
          className="hero-video"
          autoPlay 
          loop 
          muted 
          playsInline
          onLoadedData={() => console.log('✅ Video loaded successfully!')}
          onError={(e) => console.error('❌ Video failed to load:', e)}
        >
          <source src={`${process.env.PUBLIC_URL}/pasa-hero-bg-video.mp4`} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
        <div className="hero-overlay"></div>
        <div className="hero-content">
          <div className="hero-text">
            <h1 className="hero-title">
              {t('hero.title')}
            </h1>
            <p className="hero-subtitle">
              {t('hero.subtitle')}
            </p>
            <p className="hero-description">
              {t('hero.description')}
            </p>
            <div className="hero-buttons">
              <Link to="/contact" className="btn btn-primary">
                {t('hero.cta')}
              </Link>
              <Link to="/products" className="btn btn-secondary">
                {t('nav.products')}
              </Link>
            </div>
          </div>
          <div className="hero-image">
            <div className="hero-logo-container">
              <img 
                src={`${process.env.PUBLIC_URL}/logo_glass.png`}
                alt="Paşa Lebensmittel" 
                className="hero-main-logo"
                onLoad={() => console.log('✅ Logo loaded successfully!')}
                onError={(e) => {
                  console.error('❌ Logo failed to load:', e);
                  console.log('Tried path:', `${process.env.PUBLIC_URL}/logo_glass.png`);
                }}
              />
            </div>
            <div className="floating-card card-1">
              <FaLeaf />
              <span>{t('features.quality.title')}</span>
            </div>
            <div className="floating-card card-2">
              <FaTag />
              <span>{t('features.prices.title')}</span>
            </div>
          </div>
        </div>
        <div className="hero-wave">
          <svg viewBox="0 0 1440 120" xmlns="http://www.w3.org/2000/svg">
            <path 
              fill="#ffffff" 
              d="M0,64L80,69.3C160,75,320,85,480,80C640,75,800,53,960,48C1120,43,1280,53,1360,58.7L1440,64L1440,120L1360,120C1280,120,1120,120,960,120C800,120,640,120,480,120C320,120,160,120,80,120L0,120Z"
            ></path>
          </svg>
        </div>
      </section>

      {/* Partner Brands Slider */}
      <section className="partners-section">
        <div className="container">
          <h2 className="partners-title">{t('partners.title')}</h2>
          <p className="partners-subtitle">{t('partners.subtitle')}</p>
          <div className="partners-slider-container">
            <div className="partners-slider">
              {/* First set of logos */}
              {brandLogos.map((brand, index) => {
                const brandName = brand.filename.split('.')[0];
                const brandAfterImage = `${process.env.PUBLIC_URL}/brand-after/${brandName}.jpg`;
                
                return (
                  <div 
                    className="partner-logo" 
                    key={`brand-${index}`}
                    data-brand={index}
                  >
                    <div 
                      className="partner-logo-background"
                      style={{
                        backgroundImage: `url(${brandAfterImage})`
                      }}
                    ></div>
                    <img 
                      src={brand.path} 
                      alt={brand.name}
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                );
              })}
              {/* Duplicate for seamless loop */}
              {brandLogos.map((brand, index) => {
                const brandName = brand.filename.split('.')[0];
                const brandAfterImage = `${process.env.PUBLIC_URL}/brand-after/${brandName}.jpg`;
                
                return (
                  <div 
                    className="partner-logo" 
                    key={`brand-duplicate-${index}`}
                    data-brand={index}
                  >
                    <div 
                      className="partner-logo-background"
                      style={{
                        backgroundImage: `url(${brandAfterImage})`
                      }}
                    ></div>
                    <img 
                      src={brand.path} 
                      alt={brand.name}
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features">
        <div className="container">
          <h2 className="section-title">{t('features.title')}</h2>
          <div className="features-grid">
            {features.map((feature, index) => (
              <div className="feature-card" key={index}>
                <div className="feature-icon">
                  {feature.icon}
                </div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Categories Preview - Interactive Columns */}
      <section className="categories-interactive">
        <div className="categories-container">
          <div className="categories-header">
            <h2 className="section-title">{t('products.title')}</h2>
            <p className="section-subtitle">{t('products.subtitle')}</p>
          </div>
          <div className="categories-columns">
            {categories.map((category, index) => (
              <div 
                className="category-column" 
                key={index}
                data-category={index}
              >
                <div className="category-overlay"></div>
                <div className="category-content">
                  <div className="category-icon-large">
                    {category.icon}
                  </div>
                  <h3 className="category-title-vertical">{category.title}</h3>
                  <div className="category-hover-content">
                    <div className="category-icon-hover">
                      {category.icon}
                    </div>
                    <h4>{category.title}</h4>
                    <p>{t(`products.categories.${getCategoryKey(index)}.description`)}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="categories-cta">
            <Link to="/products" className="btn btn-elegant">
              {t('nav.products')}
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2>{t('hero.cta')}</h2>
            <p>{t('hero.description')}</p>
            <div className="cta-buttons">
              <Link to="/contact" className="btn btn-light">
                {t('hero.contact')}
              </Link>
              <a 
                href="https://b2b.pasalebensmittel.de" 
                className="btn btn-outline-light"
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('nav.b2b')}
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;

